/**
 * CommandPalette — TG bot /command picker.
 *
 * Lists every slash command shipped by `~/terminal_commander/tg-terminal.py`
 * (extracted into bot-commands.json). User types to filter, taps to either
 * (a) insert the command text into the composer (for commands with args),
 * or (b) send immediately (commands marked `direct`). Default is insert.
 */

import data from "../data/bot-commands.json";
import { Icon } from "./Icon";

interface BotCmd { cmd: string; desc: string; cat: string }

interface PaletteOpts {
  onCancel: () => void;
  onPick: (cmdLine: string, send: boolean) => void;
  initialQuery?: string;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// Commands that take NO args — safe to send directly on tap.
const DIRECT = new Set([
  "help", "tabs", "models", "model", "status", "memory", "active_agents",
  "work_status", "board", "claude_agent", "agents", "schedules", "secrets",
  "worklog", "sessionhandoff", "bot_learning", "claude_features",
  "cc_check", "connectors", "plugins", "restore_tabs", "resume_tab",
  "endalltabs", "hardendalltabs", "stop", "pause", "play", "wait",
  "force_restart", "survive_reboot", "menu_commands", "keys", "nokeys",
  "clear_messages", "endclaude", "restart", "windows", "saved_servers",
  "cwd", "scrollback", "eof", "bg", "compact", "recap", "agent_11_status",
  "bot_learning", "never_sleep", "allow_sleep",
]);

function rank(cmd: BotCmd, q: string): number {
  if (!q) return 1;
  const lc = cmd.cmd.toLowerCase();
  const dc = cmd.desc.toLowerCase();
  if (lc === q) return 1000;
  if (lc.startsWith(q)) return 100 + (50 - lc.length);
  if (lc.includes(q)) return 50;
  if (dc.includes(q)) return 10;
  return 0;
}

export function CommandPalette({
  onCancel,
  onPick,
  initialQuery = "",
}: PaletteOpts): HTMLElement {
  const wrap = el("div", "cmdp");
  const card = el("section", "cmdp__card");
  wrap.appendChild(card);

  // Search input
  const head = el("header", "cmdp__head");
  const searchIcon = el("span", "cmdp__head-icon");
  searchIcon.appendChild(Icon("search", 16));
  head.appendChild(searchIcon);

  const input = document.createElement("input");
  input.className = "cmdp__input";
  input.type = "text";
  input.placeholder = "Filter commands…";
  input.value = initialQuery.startsWith("/") ? initialQuery.slice(1) : initialQuery;
  head.appendChild(input);

  const close = el("button", "cmdp__close");
  close.type = "button";
  close.setAttribute("aria-label", "Cancel");
  close.appendChild(Icon("close", 16));
  close.addEventListener("click", onCancel);
  head.appendChild(close);

  card.appendChild(head);

  // List
  const list = el("div", "cmdp__list");
  card.appendChild(list);

  function render(): void {
    const q = input.value.trim().toLowerCase();
    const all = data as BotCmd[];
    const scored = all
      .map((c) => ({ c, s: rank(c, q) }))
      .filter((x) => !q || x.s > 0)
      .sort((a, b) => b.s - a.s);

    list.replaceChildren();
    if (scored.length === 0) {
      list.appendChild(el("p", "cmdp__empty", "No commands match."));
      return;
    }

    // Group by category when no query, flat list when filtering.
    if (q) {
      for (const { c } of scored) list.appendChild(buildRow(c));
    } else {
      const byCat = new Map<string, BotCmd[]>();
      for (const { c } of scored) {
        if (!byCat.has(c.cat)) byCat.set(c.cat, []);
        byCat.get(c.cat)!.push(c);
      }
      for (const [cat, cmds] of byCat) {
        list.appendChild(el("div", "cmdp__cat", cat));
        for (const c of cmds) list.appendChild(buildRow(c));
      }
    }
  }

  function buildRow(c: BotCmd): HTMLElement {
    const row = el("button", "cmdp__row");
    row.type = "button";
    row.appendChild(el("code", "cmdp__cmd", `/${c.cmd}`));
    row.appendChild(el("span", "cmdp__desc", c.desc));
    const direct = DIRECT.has(c.cmd);
    if (direct) {
      const tag = el("span", "cmdp__tag", "tap to run");
      row.appendChild(tag);
    }
    row.addEventListener("click", () => onPick(`/${c.cmd}`, direct));
    return row;
  }

  input.addEventListener("input", render);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") onCancel();
    if (e.key === "Enter") {
      const first = list.querySelector<HTMLButtonElement>(".cmdp__row");
      first?.click();
    }
  });
  wrap.addEventListener("click", (e) => {
    if (e.target === wrap) onCancel();
  });

  render();
  setTimeout(() => input.focus(), 50);

  return wrap;
}

export default CommandPalette;
